import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../axiosConfig';
import { formatPhoneNumber } from '../utils/whatsappOrder';
import CustomerFacingSubtotal from '../components/checkout/CustomerFacingSubtotal';
import { getOrderTaxName } from '../utils/merchantTax';

const WhatsAppOrderPage = () => {
  const { whatsappOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get merchant token from URL query params (for cross-domain auth)
  const searchParams = new URLSearchParams(location.search);
  const merchantToken = searchParams.get('mt');

  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [isMerchant, setIsMerchant] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    fetchOrder();
  }, [whatsappOrderId]);

  const fetchOrder = async () => {
    try {
      // Build URL with merchant token if available (for cross-domain authentication)
      // The mt (merchant token) query parameter allows authentication when cookies don't work
      // (e.g., when merchant accesses from custom domain and cookies are blocked cross-origin)
      const urlParams = merchantToken ? `?mt=${merchantToken}` : '';

      // Fetch the order details (pass merchant token if available)
      const response = await axios.get(`/whatsapp-orders/${whatsappOrderId}${urlParams}`);
      if (response.data.success) {
        setOrder(response.data.order);
        setStore(response.data.store);
        // isMerchant is determined by the backend based on cookie, auth header, or mt query token
        setIsMerchant(response.data.isMerchant);
        setEditedItems(response.data.order.items || []);
        setCustomerInfo(response.data.order.customerInfo || {
          name: '',
          phone: '',
          email: '',
          address: ''
        });
      } else {
        setError(response.data.error || 'Failed to load order');
      }
    } catch (err) {
      console.error('Order fetch error:', err);
      setError(err.response?.data?.error || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const currencyCode = order?.currency || 'NGN';
    // Don't truncate decimals - preserve exact monetary amounts
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const info = {
      PENDING: { bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', text: '#92400e', icon: '⏳', label: 'Pending' },
      CONFIRMED: { bg: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%)', text: '#1e40af', icon: '✓', label: 'Confirmed' },
      PROCESSING: { bg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', text: '#3730a3', icon: '⚙️', label: 'Processing' },
      SHIPPED: { bg: 'linear-gradient(135deg, #cffafe 0%, #67e8f9 100%)', text: '#0e7490', icon: '🚚', label: 'Shipped' },
      DELIVERED: { bg: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)', text: '#166534', icon: '✅', label: 'Delivered' },
      CANCELLED: { bg: 'linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%)', text: '#991b1b', icon: '✕', label: 'Cancelled' }
    };
    return info[status] || info.PENDING;
  };

  const getPaymentStatusInfo = (status) => {
    const info = {
      UNPAID: { bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', text: '#991b1b', icon: '💳', label: 'Unpaid' },
      PAID: { bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', text: '#166534', icon: '✓', label: 'Paid' },
      PARTIAL: { bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', text: '#92400e', icon: '◐', label: 'Partial' },
      REFUNDED: { bg: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)', text: '#374151', icon: '↩', label: 'Refunded' }
    };
    return info[status] || info.UNPAID;
  };

  const handleConfirmOrder = async () => {
    setActionLoading(true);
    try {
      // Include merchant token in URL if available (for cross-domain authentication)
      const urlParams = merchantToken ? `?mt=${merchantToken}` : '';
      const response = await axios.post(`/whatsapp-orders/${whatsappOrderId}/confirm${urlParams}`);
      if (response.data.success) {
        setOrder(response.data.order);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to confirm order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    setActionLoading(true);
    try {
      // Include merchant token in URL if available (for cross-domain authentication)
      const urlParams = merchantToken ? `?mt=${merchantToken}` : '';
      const response = await axios.post(`/whatsapp-orders/${whatsappOrderId}/cancel${urlParams}`, {
        cancelledBy: 'merchant'
      });
      if (response.data.success) {
        setOrder(response.data.order);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      // Include merchant token in URL if available (for cross-domain authentication)
      const urlParams = merchantToken ? `?mt=${merchantToken}` : '';
      const response = await axios.post(`/whatsapp-orders/${whatsappOrderId}/mark-paid${urlParams}`);
      if (response.data.success) {
        setOrder(response.data.order);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateOrder = async () => {
    setActionLoading(true);
    try {
      // Include merchant token in URL if available (for cross-domain authentication)
      const urlParams = merchantToken ? `?mt=${merchantToken}` : '';
      const response = await axios.put(`/whatsapp-orders/${whatsappOrderId}${urlParams}`, {
        items: editedItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          variations: item.variations
        })),
        customerInfo,
        isMerchantUpdate: isMerchant
      });
      if (response.data.success) {
        setOrder(response.data.order);
        setEditMode(false);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update order');
    } finally {
      setActionLoading(false);
    }
  };

  const updateItemQuantity = (index, newQty) => {
    if (newQty < 1) return;
    const updated = [...editedItems];
    updated[index].quantity = newQty;
    updated[index].total = updated[index].price * newQty;
    setEditedItems(updated);
  };

  const calculateTotal = () => {
    return editedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const generateWhatsAppLink = () => {
    if (!store?.whatsappNumber) return null;

    const phone = formatPhoneNumber(store.whatsappNumber);
    let message = `Hi ${store.name}!\n\n`;
    message += `I'd like to update my order #${order.orderNumber}\n\n`;
    message += `*ORDER DETAILS*\n\n`;

    editedItems.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      message += `   Qty: ${item.quantity}\n`;
      message += `   ${formatCurrency(item.price * item.quantity)}\n\n`;
    });

    message += `*Total: ${formatCurrency(calculateTotal())}*\n\n`;

    if (customerInfo.name || customerInfo.phone) {
      message += `*CUSTOMER INFO*\n`;
      if (customerInfo.name) message += `Name: ${customerInfo.name}\n`;
      if (customerInfo.phone) message += `Phone: ${customerInfo.phone}\n`;
      if (customerInfo.address) message += `Address: ${customerInfo.address}\n`;
    }

    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  // Loading State
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '56px',
            height: '56px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: '#64748b', fontSize: '15px', fontWeight: '500' }}>Loading order details...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
          padding: '40px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4m0 4h.01" />
            </svg>
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', color: '#1e293b' }}>
            Order Not Found
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6' }}>{error}</p>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const paymentInfo = getPaymentStatusInfo(order.paymentStatus);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '24px 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ maxWidth: '520px', margin: '0 auto' }}>

        {/* Top Badge & Merchant Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'white',
            padding: '8px 16px',
            borderRadius: '100px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
              WhatsApp Order
            </span>
          </div>

          {isMerchant && (
            <div style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '100px',
              fontSize: '11px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
            }}>
              MERCHANT
            </div>
          )}
        </div>

        {/* Main Card */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>

          {/* Store Header */}
          <div style={{
            padding: '24px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {store?.logo ? (
                <img
                  src={store.logo}
                  alt={store.name}
                  style={{
                    width: '56px',
                    height: '56px',
                    objectFit: 'contain',
                    borderRadius: '16px',
                    border: '2px solid white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              ) : (
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  {store?.name?.charAt(0) || 'S'}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h1 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#1e293b' }}>
                  {store?.name || 'Store'}
                </h1>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0', fontWeight: '500' }}>
                  Order #{order.orderNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Status Cards */}
          <div style={{
            padding: '20px 24px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px'
          }}>
            {/* Order Status */}
            <div style={{
              background: statusInfo.bg,
              padding: '16px',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>{statusInfo.icon}</span>
              <span style={{
                fontSize: '12px',
                fontWeight: '700',
                color: statusInfo.text,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {statusInfo.label}
              </span>
            </div>

            {/* Payment Status */}
            <div style={{
              background: paymentInfo.bg,
              padding: '16px',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>{paymentInfo.icon}</span>
              <span style={{
                fontSize: '12px',
                fontWeight: '700',
                color: paymentInfo.text,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {paymentInfo.label}
              </span>
            </div>
          </div>

          {/* Date */}
          <div style={{
            padding: '0 24px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span style={{ fontSize: '13px', color: '#64748b' }}>{formatDate(order.createdAt)}</span>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)', margin: '0 24px' }} />

          {/* Order Items Section */}
          <div style={{ padding: '24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#1e293b',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                  <path d="M3 6h18M16 10a4 4 0 01-8 0"/>
                </svg>
                Order Items
              </h3>
              {order.status === 'PENDING' && !editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  style={{
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    border: 'none',
                    color: '#475569',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    transition: 'all 0.2s'
                  }}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
              )}
            </div>

            {/* Items List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(editMode ? editedItems : order.items)?.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '14px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="24" height="24" fill="none" stroke="#94a3b8" strokeWidth="1.5" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <path d="M21 15l-5-5L5 21"/>
                      </svg>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontWeight: '600',
                      color: '#1e293b',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.name}
                    </p>
                    {item.variations && Object.keys(item.variations).length > 0 && (
                      <p style={{ margin: '3px 0 0', color: '#64748b', fontSize: '12px' }}>
                        {Object.entries(item.variations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </p>
                    )}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>
                        {formatCurrency(item.total && item.quantity ? item.total / item.quantity : item.price)}
                      </span>
                      <span style={{ color: '#cbd5e1' }}>×</span>
                      {editMode ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'white',
                          padding: '4px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <button
                            onClick={() => updateItemQuantity(index, item.quantity - 1)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              border: 'none',
                              background: '#f1f5f9',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            -
                          </button>
                          <span style={{
                            minWidth: '24px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#1e293b'
                          }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateItemQuantity(index, item.quantity + 1)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              border: 'none',
                              background: '#f1f5f9',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontWeight: '600', color: '#1e293b' }}>{item.quantity}</span>
                      )}
                    </div>
                  </div>
                  <p style={{
                    margin: 0,
                    fontWeight: '700',
                    color: '#1e293b',
                    fontSize: '15px',
                    whiteSpace: 'nowrap'
                  }}>
                    {formatCurrency(editMode ? item.price * item.quantity : item.total)}
                  </p>
                </div>
              ))}
            </div>

            {/* Totals Card */}
            <div style={{
              marginTop: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '16px',
              border: '1px solid #bbf7d0'
            }}>
              <CustomerFacingSubtotal
                subtotal={editMode ? calculateTotal() : order.subtotal}
                processingFee={
                  order.serviceCharge > 0
                    && String(order.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                    ? order.serviceCharge
                    : 0
                }
                formatCurrency={formatCurrency}
                compact
                rowStyle={{ marginBottom: '10px' }}
                labelStyle={{ color: '#475569', fontSize: '14px', fontWeight: 400 }}
                valueStyle={{ color: '#1e293b', fontSize: '14px', fontWeight: 500 }}
                subLabel={false}
              />
              {(order.taxTotal != null) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <span style={{ color: '#475569', fontSize: '14px' }}>{getOrderTaxName(order)}</span>
                  <span style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500' }}>
                    {formatCurrency(order.taxTotal)}
                  </span>
                </div>
              )}
              {(order.shippingFee != null || order.deliveryFee != null) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <span style={{ color: '#475569', fontSize: '14px' }}>Delivery</span>
                  <span style={{ color: '#1e293b', fontSize: '14px', fontWeight: '500' }}>
                    {formatCurrency(order.shippingFee || order.deliveryFee || 0)}
                  </span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '14px',
                borderTop: '2px dashed #86efac',
                marginTop: '4px'
              }}>
                <span style={{ fontWeight: '700', color: '#166534', fontSize: '17px' }}>Total</span>
                <span style={{ fontWeight: '800', color: '#166534', fontSize: '18px' }}>
                  {formatCurrency(editMode
                    ? calculateTotal() + (order.shippingFee || 0) + (order.taxTotal || 0) + (order.serviceCharge || 0)
                    : order.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer Info Section */}
          {(order.customerInfo?.name || order.customerInfo?.phone || editMode) && (
            <div style={{ padding: '0 24px 24px' }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Customer Information
              </h3>
              {editMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={customerInfo.name || ''}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    style={{
                      padding: '14px 18px',
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      background: '#f8fafc'
                    }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={customerInfo.phone || ''}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    style={{
                      padding: '14px 18px',
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      background: '#f8fafc'
                    }}
                  />
                  <textarea
                    placeholder="Delivery address"
                    value={customerInfo.address || ''}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                    rows={2}
                    style={{
                      padding: '14px 18px',
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'none',
                      transition: 'border-color 0.2s',
                      background: '#f8fafc'
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  padding: '16px',
                  borderRadius: '14px',
                  fontSize: '14px',
                  color: '#475569',
                  border: '1px solid #e2e8f0'
                }}>
                  {order.customerInfo?.name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <span>{order.customerInfo.name}</span>
                    </div>
                  )}
                  {order.customerInfo?.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                      <span>{order.customerInfo.phone}</span>
                    </div>
                  )}
                  {order.customerInfo?.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <path d="M22 6l-10 7L2 6"/>
                      </svg>
                      <span>{order.customerInfo.email}</span>
                    </div>
                  )}
                  {order.customerInfo?.address && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ marginTop: '2px', flexShrink: 0 }}>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>{order.customerInfo.address}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bank Transfer Details */}
          {(store?.virtualTerminal || store?.virtualAccount) && order.paymentStatus !== 'PAID' && (
            <div style={{ padding: '0 24px 24px' }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="1" y="4" width="22" height="16" rx="2"/>
                  <path d="M1 10h22"/>
                </svg>
                {store?.virtualTerminal ? 'Paystack Terminal' : 'Legacy Bank Transfer'}
              </h3>
              {store?.virtualTerminal && !store.virtualTerminal.accountNumber && (
                <p style={{ margin: '-4px 0 12px', fontSize: '13px', color: '#64748b' }}>
                  Use the Paystack checkout button above, or quote this terminal code if the seller requests it: {store.virtualTerminal.code}
                </p>
              )}
              {(store?.virtualTerminal?.accountNumber || store?.virtualAccount?.accountNumber) && <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                padding: '18px',
                borderRadius: '14px',
                border: '1px solid #93c5fd'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bank</span>
                  <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: '600', color: '#1e40af' }}>
                    {store.virtualTerminal?.bankName || store.virtualAccount?.bankName}
                  </p>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Account Name</span>
                  <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: '600', color: '#1e40af' }}>
                    {store.virtualTerminal?.accountName || store.virtualAccount?.accountName}
                  </p>
                </div>
                <div style={{
                  background: 'white',
                  padding: '14px',
                  borderRadius: '10px',
                  marginTop: '8px'
                }}>
                  <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Account Number</span>
                  <p style={{ margin: '4px 0 0', fontSize: '20px', fontWeight: '800', color: '#1e40af', letterSpacing: '1px' }}>
                    {store.virtualTerminal?.accountNumber || store.virtualAccount?.accountNumber}
                  </p>
                </div>
              </div>}
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div style={{ padding: '0 24px 24px' }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                </svg>
                Notes
              </h3>
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '16px',
                borderRadius: '14px',
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                border: '1px solid #e2e8f0'
              }}>
                {order.notes}
              </div>
            </div>
          )}

          {/* Action Buttons Section */}
          <div style={{
            padding: '24px',
            borderTop: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
          }}>

            {/* Edit Mode Actions */}
            {editMode && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditedItems(order.items);
                    setCustomerInfo(order.customerInfo || {});
                  }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '14px',
                    background: 'white',
                    color: '#475569',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateOrder}
                  disabled={actionLoading}
                  style={{
                    flex: 1,
                    padding: '16px',
                    border: 'none',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.7 : 1,
                    boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
                    transition: 'all 0.2s'
                  }}
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            {/* Merchant Actions - Improved Hierarchy */}
            {isMerchant && !editMode && order.status === 'PENDING' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Primary Action - Confirm Order */}
                <button
                  onClick={handleConfirmOrder}
                  disabled={actionLoading}
                  style={{
                    width: '100%',
                    padding: '18px',
                    border: 'none',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)',
                    transition: 'all 0.2s'
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                    <path d="M22 4L12 14.01l-3-3"/>
                  </svg>
                  {actionLoading ? 'Processing...' : 'Confirm Order'}
                </button>

                {/* Secondary Actions Row */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  {/* Mark as Paid */}
                  {order.paymentStatus !== 'PAID' && (
                    <button
                      onClick={handleMarkPaid}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        padding: '14px',
                        border: '2px solid #93c5fd',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        color: '#1e40af',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="1" y="4" width="22" height="16" rx="2"/>
                        <path d="M1 10h22"/>
                      </svg>
                      Mark Paid
                    </button>
                  )}

                  {/* Cancel Order */}
                  <button
                    onClick={handleCancelOrder}
                    disabled={actionLoading}
                    style={{
                      flex: order.paymentStatus !== 'PAID' ? 1 : 'none',
                      width: order.paymentStatus === 'PAID' ? '100%' : 'auto',
                      padding: '14px',
                      border: '2px solid #fecaca',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                      color: '#dc2626',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M15 9l-6 6M9 9l6 6"/>
                    </svg>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Customer Action - Resend to Merchant */}
            {!isMerchant && !editMode && order.status === 'PENDING' && store?.whatsappNumber && (
              <a
                href={generateWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '18px',
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: 'white',
                  borderRadius: '14px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: '700',
                  boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
                  transition: 'all 0.2s'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Resend to Merchant
              </a>
            )}

            {/* Delivered Status */}
            {order.status === 'DELIVERED' && (
              <div style={{
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                padding: '24px',
                borderRadius: '16px',
                textAlign: 'center',
                border: '1px solid #86efac'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                  boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)'
                }}>
                  <svg width="28" height="28" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3"/>
                  </svg>
                </div>
                <p style={{ margin: 0, fontWeight: '700', color: '#166534', fontSize: '17px' }}>
                  Order Delivered
                </p>
                <p style={{ margin: '6px 0 0', color: '#22c55e', fontSize: '14px' }}>
                  Thank you for your order!
                </p>
              </div>
            )}

            {/* Cancelled Status */}
            {order.status === 'CANCELLED' && (
              <div style={{
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                padding: '24px',
                borderRadius: '16px',
                textAlign: 'center',
                border: '1px solid #fca5a5'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                  boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)'
                }}>
                  <svg width="28" height="28" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M15 9l-6 6M9 9l6 6"/>
                  </svg>
                </div>
                <p style={{ margin: 0, fontWeight: '700', color: '#991b1b', fontSize: '17px' }}>
                  Order Cancelled
                </p>
                <p style={{ margin: '6px 0 0', color: '#dc2626', fontSize: '14px' }}>
                  This order has been cancelled
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '28px',
          paddingBottom: '20px'
        }}>
          <p style={{
            margin: 0,
            color: '#94a3b8',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            Powered by <span style={{ color: '#6366f1', fontWeight: '700' }}>Revstar NG</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppOrderPage;
